import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles } from "lucide-react";

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="min-h-screen bg-ff-bg pt-24 md:pt-32">
      {/* Header */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h1 className="font-serif italic text-display text-ff-text mb-8 text-balance">
            Fiction built on premises that couldn't leave us alone.
          </h1>
          
          <div className="prose text-ff-text-muted font-light leading-relaxed space-y-6">
            <p className="text-lg">
              Freddy Fiction is the serialized fiction imprint of{" "}
              <a 
                href="https://freddymedia.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-ff-text hover:text-ff-accent transition-colors underline underline-offset-4"
              >
                Freddy Media
              </a>
              {" "}— a marketing, technology, and consulting company.
            </p>
            
            <p>
              Every series starts with a question that wouldn't leave the room. We follow those questions all the way to where they actually lead. Not to where we expected. Not to where it's comfortable. To where the premise demands we go.
            </p>
            
            <p>
              We believe in serialized fiction — the specific pleasure of waiting for the next chapter, of living with characters over time, of letting a story unfold at the pace of actual life. We publish one chapter per series per week because that rhythm matters. It gives the story room to breathe and the reader time to think.
            </p>
          </div>
        </div>
      </section>

      {/* Publishing Schedule */}
      <section className="py-16 md:py-24 bg-ff-dark-section">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-serif text-2xl text-ff-text mb-8">
            Publishing Schedule
          </h2>
          
          <div className="space-y-6">
            <div className="flex items-center gap-6 p-4 border-b border-ff-border">
              <span className="w-24 text-ff-lattice font-semibold text-sm">Tuesdays</span>
              <span className="text-ff-text font-serif">The Lattice</span>
            </div>
            <div className="flex items-center gap-6 p-4 border-b border-ff-border">
              <span className="w-24 text-ff-somnium font-semibold text-sm">Thursdays</span>
              <span className="text-ff-text font-serif">SOMNIUM</span>
            </div>
            <div className="flex items-center gap-6 p-4 border-b border-ff-border">
              <span className="w-24 text-ff-celestial font-semibold text-sm">Saturdays</span>
              <span className="text-ff-text font-serif">Celestial</span>
            </div>
          </div>
          
          <p className="mt-8 text-ff-text-muted text-sm font-light">
            New chapters are published at 8:00 AM Eastern and delivered to subscribers via email.
          </p>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-serif text-2xl text-ff-text mb-8">
            The Team
          </h2>
          
          <div className="space-y-12">
            {/* Jon Frederick */}
            <div className="flex gap-6">
              <div className="w-20 h-20 rounded-full bg-ff-surface border border-ff-border shrink-0 flex items-center justify-center">
                <span className="font-serif text-2xl text-ff-text-muted">JF</span>
              </div>
              <div>
                <h3 className="font-serif text-xl text-ff-text mb-1">Jon Frederick</h3>
                <p className="text-ff-accent text-sm mb-3">Creator · The Lattice, SOMNIUM, The Overflow, ATTENDED, Article Zero, The Keys</p>
                <p className="text-ff-text-muted font-light text-sm leading-relaxed">
                  Jon is the founder of Freddy Media and the author of The Lattice and SOMNIUM. He writes fiction that starts with impossible premises and follows them to their logical conclusions. Before writing, he built software companies and asked too many questions about data patterns.
                </p>
              </div>
            </div>

            {/* Gates Blair */}
            <div className="flex gap-6">
              <div className="w-20 h-20 rounded-full bg-ff-surface border border-ff-border shrink-0 flex items-center justify-center">
                <span className="font-serif text-2xl text-ff-text-muted">GB</span>
              </div>
              <div>
                <h3 className="font-serif text-xl text-ff-text mb-1">Gates Blair</h3>
                <p className="text-ff-celestial text-sm mb-3">Author · Celestial</p>
                <p className="text-ff-text-muted font-light text-sm leading-relaxed">
                  Gates is a staff writer at Freddy Media Fiction and the author of Celestial. He writes YA sci-fi about teenagers who make questionable decisions in space and somehow survive them. Celestial was written during a stretch of nights when he couldn't sleep and needed somewhere to be.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-ff-surface">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-serif text-2xl text-ff-text mb-4">
            Ready to start reading?
          </h2>
          <p className="text-ff-text-muted font-light mb-8">
            Three complete series. Free to read. New chapters every week.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/library"
              data-testid="about-library-btn"
              className="btn-primary flex items-center gap-2"
            >
              Browse the Library
              <ChevronRight size={18} />
            </Link>
            <Link
              to="/subscribe"
              data-testid="about-subscribe-btn"
              className="btn-secondary flex items-center gap-2"
            >
              Subscribe for Updates
            </Link>
          </div>
        </div>
      </section>

      {/* Built with AI CTA */}
      <section data-testid="about-built-with-ai" className="py-16 md:py-24 bg-ff-surface border-y border-ff-border">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ff-accent/10 border border-ff-accent/20 mb-6">
            <Sparkles size={14} className="text-ff-accent" />
            <span className="text-ff-accent text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>Built with AI</span>
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-ff-text mb-4">
            This entire platform was built by Freddy Media and Freddy Compute.
          </h2>
          <p className="text-ff-text-muted font-light leading-relaxed max-w-xl mx-auto mb-8">
            Every page, every feature, every line of code — produced using AI-powered development.
            We build products like this for companies who want to move fast without compromising quality.
            If you're looking at this and thinking "I want something like this," let's talk.
          </p>
          <a
            href="https://meetings.hubspot.com/jonfred/freddy-ai-roundrobin?uuid=133f5796-273b-430b-b866-3ebd46d92af4"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-book-demo"
            className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-all hover:brightness-110 hover:-translate-y-0.5"
            style={{
              backgroundColor: "var(--ff-accent)",
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            <Sparkles size={15} />
            Book a Demo
          </a>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-ff-text-muted">
            <a href="https://freddymedia.com" target="_blank" rel="noopener noreferrer" className="hover:text-ff-text transition-colors">freddymedia.com</a>
            <span className="text-ff-border">|</span>
            <a href="https://freddycompute.com" target="_blank" rel="noopener noreferrer" className="hover:text-ff-text transition-colors">freddycompute.com</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
